/**
 * Test Connection block UI for integration discovery forms.
 *
 * Used only by: GCP Vertex AI, Agent Force, and Copilot Studio discovery forms.
 * Do not use for AWS or other integration types.
 */

import { Button, Icon, InlineMessage, Loader } from "@saviynt/design-system";
import { FormattedMessage, useIntl } from "react-intl";
import type { ConnectionStatus } from "../hooks/useTestConnection";

export interface TestConnectionBlockProps {
  isTestingConnection: boolean;
  connectionStatus: ConnectionStatus;
  onTestConnection: () => void;
  isDisabled: boolean;
}

/**
 * Shared UI for the "Test Connection" button and status messages.
 * For GCP Vertex AI, Agent Force, and Copilot Studio discovery forms only.
 */
export function TestConnectionBlock({
  isTestingConnection,
  connectionStatus,
  onTestConnection,
  isDisabled,
}: TestConnectionBlockProps) {
  const intl = useIntl();

  return (
    <div className="flex items-center gap-4 pb-4">
      <Button
        kind="outlined"
        size="medium"
        onClick={onTestConnection}
        isDisabled={isDisabled || isTestingConnection}
      >
        <FormattedMessage
          id="ELP3Cl"
          defaultMessage="Test Connection"
          description="Test connection button label"
        />
      </Button>
      {isTestingConnection && (
        <InlineMessage
          size="medium"
          colorTheme="info"
          leftIcon={<Loader kind="loop" format="inline" color="info" />}
          text={intl.formatMessage({
            id: "T13/xw",
            defaultMessage: "Testing Connection",
            description: "Testing connection message",
          })}
        />
      )}
      {connectionStatus === "success" && !isTestingConnection && (
        <InlineMessage
          size="medium"
          colorTheme="success"
          leftIcon={<Icon kind="CheckCircleFilled" />}
          text={intl.formatMessage({
            id: "TzdmbR",
            defaultMessage: "Connection Succeeded",
            description: "Connection success message",
          })}
        />
      )}
      {connectionStatus === "failed" && !isTestingConnection && (
        <InlineMessage
          size="medium"
          colorTheme="critical"
          leftIcon={<Icon kind="AlertCriticalFilled" />}
          text={intl.formatMessage({
            id: "CPldkc",
            defaultMessage: "Connection Failed",
            description: "Connection failed message",
          })}
        />
      )}
    </div>
  );
}
